// The board store: wraps the WASM engine, tells the UI when something changed and hands local
// updates to the sync client.
import type { Timer } from "./timer";
import type { Stroke } from "./engine-api";
import type { ViewArea } from "./views";
import { CARD_STRIDE, LINK_STRIDE, type CardInfo, type Engine, type FilterSpec, type FrameLayout, type FrameSpec, type LaneLoad, type LinkInfo, type LinkKind, type Status, type StatusCounts, type BoardImage } from "./engine-api";

export interface NewCard { x: number; y: number; w?: number; h?: number; title?: string; color?: number; owner?: string; due?: string }

/** local/remote: the board changed; view: only this viewer's filter changed. */
export type ChangeSource = "local" | "remote" | "view";
type Listener = (source: ChangeSource) => void;

export const emptyFilter = (): FilterSpec => ({ statuses: [], colors: [], owners: [], due: null, text: "" });
export const filterActive = (f: FilterSpec) =>
  f.statuses.length > 0 || f.colors.length > 0 || f.owners.length > 0 || f.due !== null || f.text.trim() !== "";

export class Store {
  private listeners = new Set<Listener>();
  private framesCache: FrameSpec[] | null = null;
  private imagesCache: BoardImage[] | null = null;
  private strokesCache: Stroke[] | null = null;
  private loadsCache: Map<string, LaneLoad> | null = null;
  /** Increments on every change; renderers and caches compare against it. */
  version = 0;
  /** Called after each local change, so the sync client can send it right away. */
  onLocalChange: () => void = () => {};
  /** Readers cannot edit (the server drops their updates anyway); every write below is a no-op. */
  readOnly = false;

  constructor(readonly engine: Engine) {}

  /** Writers of pending edits (the description editor): run before the board changes otherwise. */
  private flushers = new Set<() => void>();
  private flushing = false;
  /** Registers a writer of a pending edit; returns the unregister function. */
  beforeChange(fn: () => void) {
    this.flushers.add(fn);
    return () => { this.flushers.delete(fn); };
  }
  private holds = 0;
  private held: Uint8Array[] = [];
  /** Holds back others' updates (an input method composing text in the editor) until released. */
  holdRemote(): () => void {
    this.holds++;
    let done = false;
    return () => {
      if (done) return;
      done = true;
      if (--this.holds === 0 && this.held.length) {
        const q = this.held;
        this.held = [];
        this.flushPending();
        try {
          for (const b of q) {
            try { this.engine.import(b); } catch (e) { console.warn("held update not imported", e); }
          }
        } finally { this.changed("remote"); }
      }
    };
  }
  private flushPending() {
    if (this.flushing) return;
    this.flushing = true;
    try { for (const f of this.flushers) f(); } finally { this.flushing = false; }
  }

  subscribe(fn: Listener) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private changed(source: ChangeSource) {
    if (source !== "view") {
      this.version++;
      this.framesCache = null;
      this.imagesCache = null;
      this.strokesCache = null;
      this.loadsCache = null;
    }
    if (source === "local") this.onLocalChange();
    for (const l of this.listeners) l(source);
  }

  /** The viewer's filter; not part of the board document, never synced. */
  filter: FilterSpec = emptyFilter();
  setFilter(f: FilterSpec) {
    this.filter = f;
    this.engine.setFilter(JSON.stringify(f));
    this.changed("view");
  }

  /** Runs a local edit and notifies once. */
  edit<T>(fn: (e: Engine) => T): T {
    if (this.readOnly) return undefined as T;
    this.flushPending();
    const r = fn(this.engine);
    this.changed("local");
    return r;
  }

  importRemote(bytes: Uint8Array) {
    if (this.holds > 0) { this.held.push(bytes.slice()); return; }
    this.flushPending();
    this.engine.import(bytes);
    this.changed("remote");
  }

  // ---- reads ----
  frames(): FrameSpec[] {
    if (!this.framesCache) this.framesCache = JSON.parse(this.engine.framesJson()) as FrameSpec[];
    return this.framesCache;
  }
  card(id: string): CardInfo | null {
    const j = this.engine.cardJson(id);
    return j ? (JSON.parse(j) as CardInfo) : null;
  }
  cardCount() { return this.engine.cardCount(); }
  boardTitle() { return this.engine.boardTitle(); }
  boardArchived() { return this.engine.boardArchived(); }
  boardKind(): "project" | "team" { return this.engine.boardKind() === "team" ? "team" : "project"; }
  /** Only when setting up a new board (main.ts); later board admins change it on the start page. */
  setBoardKind(kind: "project" | "team") { this.edit((e) => e.setBoardKind(kind)); }
  /** Lane loads (X5) by `frame\tlane\tweek` ("2026-W43"); rebuilt after each change. */
  loads(): Map<string, LaneLoad> {
    if (!this.loadsCache) {
      this.loadsCache = new Map();
      // most boards have no capacities: no engine call then
      if (!this.frames().some((f) => f.rows.some((r) => r.capacity))) return this.loadsCache;
      for (const l of JSON.parse(this.engine.laneLoadsJson()) as LaneLoad[]) this.loadsCache.set(`${l.frame}\t${l.lane}\t${l.week}`, l);
    }
    return this.loadsCache;
  }
  views(): ViewArea[] { return JSON.parse(this.engine.viewsJson()) as ViewArea[]; }
  defaultView(): ViewArea | null { return this.views().find((v) => v.default) ?? null; }
  saveView(id: string, name: string, x: number, y: number, w: number, h: number): boolean { return this.edit((e) => e.saveView(id, name, x, y, w, h)) ?? false; }
  deleteView(id: string) { this.edit((e) => e.deleteView(id)); }
  setDefaultView(id: string) { this.edit((e) => e.setDefaultView(id)); }
  /** The board's meeting timer (E4), null when there is none. */
  timer(): Timer | null {
    const j = this.engine.timerJson();
    return j ? (JSON.parse(j) as Timer) : null;
  }
  hitTest(x: number, y: number) { return this.engine.hitTest(x, y) ?? null; }
  queryIds(x: number, y: number, w: number, h: number): string[] {
    const s = this.engine.queryIds(x, y, w, h);
    return s ? s.split("\n") : [];
  }
  bounds(): [number, number, number, number] | null {
    const b = this.engine.bounds();
    return b.length === 4 ? [b[0], b[1], b[2], b[3]] : null;
  }
  /** Infobar counts of the cards passing the filter. */
  counts(): StatusCounts {
    return JSON.parse(this.engine.statusCounts()) as StatusCounts;
  }
  /** Bounds of the cards passing the filter, null when nothing matches. */
  matchBounds(): [number, number, number, number] | null {
    const b = this.engine.matchBounds();
    return b.length === 4 ? [b[0], b[1], b[2], b[3]] : null;
  }
  link(id: string): LinkInfo | null {
    const j = this.engine.linkJson(id);
    return j ? (JSON.parse(j) as LinkInfo) : null;
  }
  linkCount() { return this.engine.linkCount(); }
  conflictCount() { return this.engine.conflictCount(); }
  conflictBounds(): [number, number, number, number] | null {
    const b = this.engine.conflictBounds();
    return b.length === 4 ? [b[0], b[1], b[2], b[3]] : null;
  }
  /** Links near the viewport packed as floats (see LINK_STRIDE) plus a lazy [id, from, to] list. */
  visibleLinks(x: number, y: number, w: number, h: number) {
    const buf = this.engine.visibleLinks(x, y, w, h);
    let ids: [string, string, string][] | null = null;
    return {
      buf,
      count: buf.length / LINK_STRIDE,
      ids: () => (ids ??= buf.length ? this.engine.visibleLinkIds().split("\n").map((l) => l.split("\t") as [string, string, string]) : []),
    };
  }
  owners(): string[] {
    const s = this.engine.owners();
    return s ? s.split("\n") : [];
  }
  /** Visible cards packed as floats (see CARD_STRIDE) plus a lazy id list. */
  visible(x: number, y: number, w: number, h: number) {
    const buf = this.engine.visibleCards(x, y, w, h);
    let ids: string[] | null = null;
    return {
      buf,
      count: buf.length / CARD_STRIDE,
      ids: () => (ids ??= buf.length ? this.engine.visibleIds().split("\n") : []),
      text: () => JSON.parse(this.engine.visibleText()) as [string, string, string, string, number, string, number | null][],
    };
  }

  // ---- writes (each one undo step) ----
  createCard(c: NewCard): string { return this.edit((e) => e.createCard(JSON.stringify({ w: 100, h: 100, ...c }))); }
  moveCards(ids: string[], dx: number, dy: number) { if (ids.length) this.edit((e) => e.moveCards(ids.join("\n"), dx, dy, true)); }
  setTitle(id: string, t: string) { this.edit((e) => e.setTitle(id, t)); }
  resizeCard(id: string, w: number, h: number) { this.edit((e) => e.resizeCard(id, w, h)); }

  // ---- images (H1) ----
  images(): BoardImage[] { return (this.imagesCache ??= JSON.parse(this.engine.imagesJson()) as BoardImage[]); }
  image(id: string): BoardImage | null { return this.images().find((i) => i.id === id) ?? null; }
  imageAt(x: number, y: number): string | null { return this.engine.imageAt(x, y) ?? null; }
  /** The new image's id, or null when refused (or read-only). */
  addImage(file: string, x: number, y: number, w: number, h: number): string | null { return this.edit((e) => e.addImage(file, x, y, w, h)) || null; }
  moveImages(ids: string[], dx: number, dy: number) { if (ids.length) this.edit((e) => e.moveImages(ids.join("\n"), dx, dy)); }
  resizeImage(id: string, w: number, h: number) { this.edit((e) => e.resizeImage(id, w, h)); }
  deleteImages(ids: string[]) { if (ids.length) this.edit((e) => e.deleteImages(ids.join("\n"))); }
  imageTo(id: string, front: boolean) { this.edit((e) => e.imageTo(id, front)); }

  // ---- drawings (ink) ----
  strokes(): Stroke[] { return (this.strokesCache ??= JSON.parse(this.engine.strokesJson()) as Stroke[]); }
  /** The new stroke's id, or null when refused (or read-only). */
  addStroke(pts: number[], color: number, kind: number): string | null { return this.edit((e) => e.addStroke(new Float64Array(pts), color, kind)) || null; }
  moveStrokes(ids: string[], dx: number, dy: number) { if (ids.length) this.edit((e) => e.moveStrokes(ids.join("\n"), dx, dy)); }
  deleteStrokes(ids: string[]) { if (ids.length) this.edit((e) => e.deleteStrokes(ids.join("\n"))); }
  setBoardTitle(t: string) { this.edit((e) => e.setBoardTitle(t)); }
  /** Sets or removes (null) the meeting timer – shared, not undoable. False when refused or read-only. */
  setTimer(t: Timer | null): boolean { return this.edit((e) => e.setTimer(t ? JSON.stringify(t) : "")) ?? false; }
  // a multi-selection is changed in one engine call = one undo step (C13)
  setStatus(ids: string[], s: Status) { if (ids.length) this.edit((e) => e.setStatusOf(ids.join("\n"), s)); }
  setColor(ids: string[], c: number) { if (ids.length) this.edit((e) => e.setColorOf(ids.join("\n"), c)); }
  setOwner(ids: string[], o: string) { if (ids.length) this.edit((e) => e.setOwnerOf(ids.join("\n"), o)); }
  setDue(ids: string[], due: string) { if (ids.length) this.edit((e) => e.setDueOf(ids.join("\n"), due)); }
  /** Story points; false when the number is refused (not 0–999). Empty removes them. */
  setPoints(ids: string[], points: string): boolean { return ids.length > 0 && (this.edit((e) => e.setPointsOf(ids.join("\n"), points.trim())) ?? 0) > 0; }
  /** False when the link is refused (only http(s) and mailto). Empty removes it. */
  setLink(id: string, link: string): boolean { return this.edit((e) => e.setLink(id, link)) ?? false; }
  setAssignees(id: string, list: string[]) { this.edit((e) => e.setAssignees(id, list.join("\n"))); }
  /** The description as plain text (no formatting, no pictures). */
  notes(id: string): string { return this.engine.notes(id); }
  /** The description as an editor document (JSON, see rich.ts / lanewerk-core board/rich.rs). */
  notesRich(id: string): string { return this.engine.notesRich(id); }
  /** Applies one editor change (JSON delta) in one undo step; false when refused or nothing changed. */
  editNotesRich(id: string, delta: string): boolean { return this.edit((e) => e.editNotesRich(id, delta)) ?? false; }
  align(id: string) { return this.edit((e) => e.alignWithDueDate(id)); }
  /** Clip (JSON) of the cards and the links among them, for the clipboard (see lanewerk-core board/clip.rs). */
  copyCards(ids: string[]): string { return ids.length ? this.engine.copyCards(ids.join("\n")) : ""; }
  /** Pastes a clip moved by (dx, dy) in one undo step; the new card ids. */
  pasteCards(json: string, dx: number, dy: number): string[] {
    const r = this.edit((e) => e.pasteCards(json, dx, dy));
    return r ? r.split("\n") : [];
  }
  duplicateCards(ids: string[], dx: number, dy: number): string[] {
    if (!ids.length) return [];
    const r = this.edit((e) => e.duplicateCards(ids.join("\n"), dx, dy));
    return r ? r.split("\n") : [];
  }
  deleteCards(ids: string[]) { if (ids.length) this.edit((e) => e.deleteCards(ids.join("\n"))); }
  /** Returns the new link id, or null when the cards are already linked (or the same card). */
  createLink(from: string, to: string, kind: LinkKind = 1): string | null {
    if (this.readOnly) return null;
    const id = this.engine.createLink(from, to, kind);
    if (id) this.changed("local");
    return id ?? null;
  }
  deleteLinks(ids: string[]) { if (ids.length) this.edit((e) => e.deleteLinks(ids.join("\n"))); }
  setLinkStyle(id: string, color: number, dashed: boolean) { this.edit((e) => e.setLinkStyle(id, color, dashed)); }
  setLinkKind(id: string, kind: LinkKind) { this.edit((e) => e.setLinkKind(id, kind)); }
  reverseLink(id: string) { this.edit((e) => e.reverseLink(id)); }
  addSprintFrame(id: string, title: string, x: number, y: number, year: number, week: number, weeks: number, domains: string[]) {
    this.edit((e) => e.addSprintFrame(id, title, x, y, year, week, weeks, domains.join("|")));
  }
  moveFrame(id: string, x: number, y: number) { this.edit((e) => e.moveFrame(id, x, y)); }
  frameLayout(id: string): FrameLayout | null {
    const j = this.engine.frameLayout(id);
    return j ? (JSON.parse(j) as FrameLayout) : null;
  }
  /** One undo step; false when the layout is invalid. */
  setFrameLayout(id: string, l: FrameLayout): boolean {
    if (this.readOnly) return false;
    const ok = this.engine.setFrameLayout(id, JSON.stringify(l));
    if (ok) this.changed("local");
    return ok;
  }
  addFrame(id: string, x: number, y: number, l: FrameLayout): boolean {
    if (this.readOnly) return false;
    const ok = this.engine.addFrame(id, x, y, JSON.stringify(l));
    if (ok) this.changed("local");
    return ok;
  }
  /** In front of / behind all other frames (one undo step). */
  frameTo(id: string, front: boolean) { this.edit((e) => e.frameTo(id, front)); }
  deleteFrame(id: string) { this.edit((e) => e.deleteFrame(id)); }
  undo() { return this.edit((e) => e.undo()); }
  redo() { return this.edit((e) => e.redo()); }
}
